class I18n {
  constructor() {
    this.currentLanguage = 'en'; // 默认语言
    this.currentRegion = 'us';   // 默认地区
    this.translations = {};
    this.regionConfig = {};
    this.markets = [];
    // 支持的语言列表
    this.supportedLanguages = ['en'];
    // 支持的地区
    this.supportedRegions = ['us'];

    // 显示名称映射
    this.regionNames = {
      us: 'United States',
      hk: 'Hong Kong SAR',
      fr: 'France'
    };
    this.languageNames = {
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
      es: 'Español',
      it: 'Italiano',
      nl: 'Nederlands',
      sv: 'Svenska',
      pt: 'Português',
      ja: '日本語',
      'zh-Hant': '繁體中文',
      'zh-Hans': '简体中文'
    };

    // BCP47 语言标签（用于 html lang 和 hreflang）
    this.bcp47 = {
      'us-en': 'en-US',
      'us-es': 'es-US',
      'us-zh-Hans': 'zh-Hans-US',
      'gb-en': 'en-GB',
      'ca-en': 'en-CA',
      'ca-fr': 'fr-CA',
      'fr-fr': 'fr-FR',
      'de-de': 'de-DE',
      'es-es': 'es-ES',
      'it-it': 'it-IT',
      'nl-nl': 'nl-NL',
      'se-sv': 'sv-SE',
      'mx-es': 'es-MX',
      'br-pt': 'pt-BR',
      'sg-en': 'en-SG',
      'sg-zh-Hans': 'zh-Hans-SG',
      'hk-en': 'en-HK',
      'hk-zh-Hant': 'zh-Hant-HK',
      'jp-ja': 'ja-JP'
    };
  }

  // 初始化，检测用户语言
  async init() {
    try {
      // 从路径或存储检测地区/语言
      this.detectLocale();
      
      // 加载翻译和地区配置
      await Promise.all([
        this.loadTranslations(),
        this.loadRegionConfig()
      ]);
      
      // 应用翻译到页面
      this.applyTranslations();
      // 应用地区化内容
      this.applyRegionContent();

      // 设置 html 的 lang 属性
      this.applyHtmlLang();

      // 更新底部展示当前语言/地区
      this.updateFooterLocaleDisplay();
    } catch (error) {
      console.error('i18n initialization error:', error);
    }
  }

  // 加载市场配置
  async loadMarkets() {
    try {
      const res = await fetch('i18n/markets.json');
      if (!res.ok) return;
      this.markets = await res.json();
    } catch (_) {}
  }

  // 渲染页脚语言/地区面板
  renderLocalePanel() {
    try {
      const grid = document.querySelector('#footer-locale-panel .locale-grid');
      if (!grid || !Array.isArray(this.markets) || !this.markets.length) return;
      grid.innerHTML = '';
      const locales = this.markets.filter(m => m.enabled !== false);
      const frag = document.createDocumentFragment();
      locales.forEach((m) => {
        const row = document.createElement('div');
        row.className = 'locale-row';
        const flag = document.createElement('span');
        flag.className = 'locale-flag';
        flag.textContent = m.flag || '';
        const textWrap = document.createElement('span');
        textWrap.className = 'locale-text';
        const region = document.createElement('span');
        region.className = 'locale-region';
        region.textContent = m.name || m.code.toUpperCase();
        const langs = document.createElement('span');
        langs.className = 'locale-langs';

        (m.languages || []).forEach((langCode, idx) => {
          const btn = document.createElement('button');
          btn.className = 'lang-option';
          btn.setAttribute('data-region', m.code);
          btn.setAttribute('data-lang', langCode);
          btn.textContent = this.languageNames[langCode] || langCode;
          langs.appendChild(btn);
          if (idx < (m.languages.length - 1)) {
            const sep = document.createElement('span');
            sep.className = 'lang-sep';
            sep.setAttribute('aria-hidden', 'true');
            langs.appendChild(sep);
          }
        });

        textWrap.appendChild(region);
        textWrap.appendChild(langs);
        row.appendChild(flag);
        row.appendChild(textWrap);
        row.appendChild(document.createElement('span')).className = 'locale-current';
        frag.appendChild(row);
      });
      grid.appendChild(frag);
    } catch (e) {
      // no-op
    }
  }

  // 为每个 .locale-langs 在相邻语言按钮之间插入分隔线元素
  ensureLangSeparators() {
    try {
      document.querySelectorAll('.locale-langs').forEach((wrap) => {
        const children = Array.from(wrap.children);
        // 已有分隔线则跳过，防止重复插入
        if (children.some((el) => el.classList && el.classList.contains('lang-sep'))) return;
        const buttons = Array.from(wrap.querySelectorAll('.lang-option'));
        if (buttons.length <= 1) return;
        // 依次插入分隔线
        for (let i = 0; i < buttons.length - 1; i++) {
          const sep = document.createElement('span');
          sep.className = 'lang-sep';
          sep.setAttribute('aria-hidden', 'true');
          buttons[i].after(sep);
        }
      });
    } catch (_) {}
  }

  // 检测当前 locale（优先路径前缀，如 /hk-en/...），其次 localStorage，最后浏览器语言
  detectLocale() {
    this.currentLanguage = 'en';
    this.currentRegion = 'us';
    try {
      localStorage.removeItem('preferredLocale');
    } catch (_) {}
  }

  // 确保存在 <base href="/">，避免 /xx-yy 下相对资源路径 404
  ensureBaseHref() {
    try {
      const head = document.head || document.getElementsByTagName('head')[0];
      let base = head.querySelector('base');
      if (!base) {
        base = document.createElement('base');
        head.insertBefore(base, head.firstChild);
      }
      base.setAttribute('href', '/');
    } catch (_) {}
  }

  // 加载翻译文件
  async loadTranslations() {
    try {
      // 使用相对路径，确保在开发环境和生产环境都能正常工作
      const response = await fetch(`i18n/translations/${this.currentLanguage}.json`);
      if (!response.ok) {
        throw new Error(`Translation file not found for ${this.currentLanguage}`);
      }
      this.translations = await response.json();
    } catch (error) {
      console.warn(`Translation file for ${this.currentLanguage} not found, falling back to English`);
      try {
        const response = await fetch('i18n/translations/en.json');
        this.translations = await response.json();
        this.currentLanguage = 'en';
      } catch (fallbackError) {
        console.error('Failed to load fallback English translations:', fallbackError);
        // 使用默认翻译对象，避免空白页面
        this.translations = {
          common: {
            nav_home: 'Home',
            nav_products: 'Products',
            nav_merchants: 'For Merchants',
            nav_users: 'For Users'
          },
          hero: {
            title: 'Portable Charger Rental',
            subtitle: 'Stay powered wherever you go'
          },
          products: {
            title: 'Our Products',
            view_details: 'View Details',
            add_to_cart: 'Add to Cart'
          }
        };
      }
    }
  }

  // 加载地区配置文件
  async loadRegionConfig() {
    try {
      const response = await fetch(`i18n/regions/${this.currentRegion}.json`);
      if (!response.ok) {
        throw new Error(`Region config not found for ${this.currentRegion}`);
      }
      this.regionConfig = await response.json();
    } catch (error) {
      console.warn(`Region config for ${this.currentRegion} not found, using default`);
      try {
        const response = await fetch('i18n/regions/us.json');
        this.regionConfig = await response.json();
        this.currentRegion = 'us';
      } catch (fallbackError) {
        console.error('Failed to load fallback region config:', fallbackError);
        // 使用默认地区配置
        this.regionConfig = {
          region_name: 'United States',
          currency: 'USD',
          recommended_products: [],
          promotions: []
        };
      }
    }
  }

  // 应用翻译到页面
  applyTranslations() {
    try {
      // 查找所有带data-i18n属性的元素
      document.querySelectorAll('[data-i18n]').forEach(el => {
        const key = el.getAttribute('data-i18n');
        const translation = this.getValueByKey(this.translations, key);
        if (translation) {
          el.textContent = translation;
        }
      });
    } catch (error) {
      console.error('Error applying translations:', error);
    }
  }

  // 设置 html 的 lang 属性
  applyHtmlLang() {
    try {
      const code = this.bcp47[`${this.currentRegion}-${this.currentLanguage}`] || this.currentLanguage;
      document.documentElement.setAttribute('lang', code);
    } catch (e) {
      // no-op
    }
  }

  // 应用地区化内容
  applyRegionContent() {
    try {
      // 更新推荐产品
      if (this.regionConfig.recommended_products) {
        this.updateRecommendedProducts(this.regionConfig.recommended_products);
      }
      
      // 更新其他地区化内容
      if (this.regionConfig.promotions) {
        this.updatePromotions(this.regionConfig.promotions);
      }
    } catch (error) {
      console.error('Error applying region content:', error);
    }
  }

  // 根据键获取翻译值
  getValueByKey(obj, key) {
    if (!key || !obj) return null;
    return key.split('.').reduce((o, i) => o && o[i], obj);
  }

  // 更新推荐产品（简化版实现，防止报错）
  updateRecommendedProducts(productIds) {
    try {
      // 检查元素是否存在
      const productSection = document.querySelector('.recommended-products');
      if (!productSection) return;
      
      // 简化实现，实际项目中需要替换为真实的产品数据
      console.log('Updating recommended products for region:', this.currentRegion, productIds);
    } catch (error) {
      console.error('Error updating recommended products:', error);
    }
  }

  // 更新促销信息（简化版实现，防止报错）
  updatePromotions(promotions) {
    try {
      const promotionSection = document.querySelector('.promotions');
      if (!promotionSection) return;
      
      // 简化实现
      console.log('Updating promotions for region:', this.currentRegion, promotions);
    } catch (error) {
      console.error('Error updating promotions:', error);
    }
  }

  // 切换语言的公共方法（兼容旧接口）
  async changeLanguage(language) {
    try {
      if (!this.supportedLanguages.includes(language)) {
        console.warn(`Language ${language} not supported`);
        return;
      }
      this.changeLocale(this.currentRegion, language);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  }

  // 切换地区的公共方法
  async changeRegion(region) {
    try {
      this.currentRegion = region;
      await this.loadRegionConfig();
      this.applyRegionContent();
      this.changeLocale(region, this.currentLanguage);
    } catch (error) {
      console.error('Error changing region:', error);
    }
  }

  // 统一的切换语言/地区方法
  changeLocale(region, language) {
    if (region !== 'us' || language !== 'en') return;
    this.currentRegion = 'us';
    this.currentLanguage = 'en';
    this.applyHtmlLang();
    this.updateFooterLocaleDisplay();
  }

  // 持久化保存选择
  persistLocale() {
    try {
      localStorage.setItem('preferredLocale', JSON.stringify({
        region: this.currentRegion,
        language: this.currentLanguage
      }));
    } catch (_) {}
  }

  // 生成加上本地化前缀的当前页面路径
  buildLocalizedPath(region, language) {
    const prefix = `/${region}-${language}`;
    // 获取当前去掉现有本地化前缀后的相对路径
    const path = window.location.pathname || '/';
    const match = path.match(/^\/([a-z]{2})-([a-z]{2})(\/.*)?$/i);
    let remainder = '';
    if (match) {
      remainder = match[3] || '/';
    } else {
      remainder = path;
    }
    // 规范首页
    if (remainder === '/' || remainder === '') {
      return `${prefix}/`;
    }
    return `${prefix}${remainder}`;
  }

  // 为站内链接添加本地化前缀
  prefixInternalLinks() {
    const prefix = `/${this.currentRegion}-${this.currentLanguage}`;
    const isExternal = (href) => /^(https?:)?\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');
    const normalize = (href) => href.replace(/^\/+/, '/');
    document.querySelectorAll('a[href]')?.forEach((a) => {
      const href = a.getAttribute('href');
      if (!href) return;
      if (href.startsWith('#')) return; // 锚点
      if (isExternal(href)) return; // 外链
      // 已包含本地化前缀则忽略
      if (/^\/[a-z]{2}-[a-z]{2}\//i.test(href) || /^\/[a-z]{2}-[a-z]{2}\/?$/i.test(href)) return;
      // 相对文件，如 about.html、/about.html
      if (/^\//.test(href)) {
        a.setAttribute('href', normalize(`${prefix}${href}`));
      } else {
        // 相对相对路径，如 "about.html"
        // 简化：统一转绝对
        a.setAttribute('href', `${prefix}/${href}`);
      }
    });
  }

  // 注入页面 hreflang
  injectHreflangLinks() {
    try {
      // 清理旧的
      document.querySelectorAll('link[rel="alternate"][data-i18n-alt]')?.forEach((el) => el.remove());

      const path = window.location.pathname || '/';
      const match = path.match(/^\/([a-z]{2})-([a-z]{2})(\/.*)?$/i);
      const remainder = match ? (match[3] || '/') : path;
      const head = document.head || document.getElementsByTagName('head')[0];

      const locales = [
        { region: 'us', language: 'en' },
        { region: 'hk', language: 'en' },
        { region: 'fr', language: 'fr' },
      ];

      locales.forEach(({ region, language }) => {
        const href = `/${region}-${language}${remainder === '/' ? '/' : remainder}`;
        const hreflang = this.bcp47[`${region}-${language}`] || language;
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', hreflang);
        link.setAttribute('href', href);
        link.setAttribute('data-i18n-alt', 'true');
        head.appendChild(link);
      });

      // x-default
      const xdef = document.createElement('link');
      xdef.setAttribute('rel', 'alternate');
      xdef.setAttribute('hreflang', 'x-default');
      xdef.setAttribute('href', `/us-en${remainder === '/' ? '/' : remainder}`);
      xdef.setAttribute('data-i18n-alt', 'true');
      head.appendChild(xdef);
    } catch (e) {
      // no-op
    }
  }

  // 更新底部显示的“地区 | 语言”
  updateFooterLocaleDisplay() {
    const el = document.getElementById('footer-locale-current');
    if (!el) return;
    const regionLabel = this.regionNames[this.currentRegion] || this.currentRegion.toUpperCase();
    const languageLabel = this.languageNames[this.currentLanguage] || this.currentLanguage.toUpperCase();
    el.textContent = `${regionLabel} | ${languageLabel}`;
  }

  // 绑定底部语言/地区选择器
  bindFooterLocaleEvents() {
    const toggle = document.getElementById('footer-locale-toggle');
    const panel = document.getElementById('footer-locale-panel');
    if (!toggle || !panel) return;

    const setExpanded = (v) => toggle.setAttribute('aria-expanded', v ? 'true' : 'false');
    const close = () => { panel.classList.remove('is-open'); setExpanded(false); };
    const open = () => { panel.classList.add('is-open'); setExpanded(true); };

    toggle.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (panel.classList.contains('is-open')) close(); else open();
    });

    document.addEventListener('click', (e) => {
      if (!panel.contains(e.target) && e.target !== toggle) close();
    });

    panel.querySelectorAll('[data-region][data-lang]')?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const region = btn.getAttribute('data-region');
        const lang = btn.getAttribute('data-lang');
        this.changeLocale(region, lang);
      });
    });
  }

  // 在面板内标记当前项
  updateFooterLocaleSelection() {
    const panel = document.getElementById('footer-locale-panel');
    if (!panel) return;
    // 清除行选中态
    panel.querySelectorAll('.locale-row')?.forEach((row) => row.classList.remove('is-current'));
    // 标记语言按钮与对应行
    panel.querySelectorAll('[data-region][data-lang]')?.forEach((btn) => {
      const isCurrent = btn.getAttribute('data-region') === this.currentRegion && btn.getAttribute('data-lang') === this.currentLanguage;
      btn.classList.toggle('is-current', !!isCurrent);
      if (isCurrent) btn.setAttribute('aria-current', 'true'); else btn.removeAttribute('aria-current');
      if (isCurrent) {
        const row = btn.closest('.locale-row');
        if (row) row.classList.add('is-current');
      }
    });
  }
}

// 创建单例实例
export const i18n = new I18n();
